import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, User, FileText, Shield, Check } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getSavedUtmParams } from '../../utils/utm';
import './LeadCaptureModal.css';

const API_BASE = window.location.hostname === 'test.maxiomwealth.com'
  ? 'https://regular.docker.jama.co.in/app/api'
  : 'https://regular.docker.jama.co.in/app/api';

const SENDY_SUBSCRIBE_URL = "https://regular.maxiom.in/app/api/newsendy/";

const LeadCaptureModal = ({
  isOpen,
  onClose,
  calculatorType,
  calculatorResults,
  onLeadCaptured
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // OTP state
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Benefits based on calculator type
  const getBenefits = () => {
    const benefitsMap = {
      sip: [
        'Personalized SIP portfolio recommendations',
        'Expected returns analysis',
        'Tax-saving ELSS fund suggestions',
        'Free consultation with investment expert'
      ],
      tax: [
        'Complete tax calculation breakdown',
        '80C to 80G deduction strategies',
        'Tax-saving investment recommendations',
        'Personalized tax planning session'
      ],
      retirement: [
        'Detailed retirement roadmap',
        'Monthly investment requirements',
        'Inflation-adjusted projections',
        'Expert retirement planning consultation'
      ],
      default: [
        'Personalized investment recommendations',
        'Detailed analysis report',
        'Expert consultation call',
        'Exclusive investment tips'
      ]
    };

    return benefitsMap[calculatorType] || benefitsMap.default;
  };

  const getHeadline = () => {
    const headlines = {
      sip: 'Get Your Personalized SIP Investment Plan',
      tax: 'Get Your Complete Tax Saving Report',
      retirement: 'Get Your Retirement Planning Blueprint',
      default: 'Get Your Personalized Investment Report'
    };

    return headlines[calculatorType] || headlines.default;
  };

  // Send OTP
  const sendOTP = async (phoneNumber = null) => {
    const mobileToUse = phoneNumber || formData.phone;

    if (mobileToUse.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setOtpLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v2/mobile_otp_verfication/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_mobile_number: mobileToUse }),
      });

      const data = await response.json();

      if (data.resp_code === '100') {
        setOtpSent(true);
        toast.success('OTP sent successfully!');
      } else {
        toast.error(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP send error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const verifyOTP = async (otpCode = null) => {
    const otpToUse = otpCode || otp;

    if (otpToUse.length !== 4) {
      toast.error('Please enter a 4-digit OTP');
      return;
    }

    setOtpLoading(true);
    try {
      const response = await fetch(`${API_BASE}/v2/mobile_otp_verfication/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_mobile_number: formData.phone, otp: otpToUse }),
      });

      const data = await response.json();

      if (data.result === true) {
        setOtpVerified(true);
        toast.success('Mobile number verified successfully!');
      } else {
        toast.error(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email is mandatory
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number';
    }

    if (!otpVerified) {
      newErrors.phone = 'Please verify your mobile number with OTP';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send data to newsendy API (same as contact-us page)
  const sendToSendy = async () => {
    // Step 1: Initialize session
    console.log("[DEBUG] Initializing sendy session...");
    const sessionResponse = await fetch(SENDY_SUBSCRIBE_URL, {
      method: 'GET',
      credentials: 'include'
    });

    const cookies = sessionResponse.headers.get('set-cookie') || '';
    console.log("[DEBUG] Session cookies:", cookies);

    // Step 2: Prepare payload
    const customer_message = formData.name
      ? `${formData.name} requested report from ${calculatorType} calculator.`
      : `Customer requested report from ${calculatorType} calculator.`;

    const params = new URLSearchParams();
    params.append('email', formData.email);
    params.append('mobile_number', formData.phone);
    params.append('start_my_switch', 'false');
    params.append('name', formData.name);
    params.append('contact_us_information[]', 'calculator-report');
    params.append('message', customer_message);
    params.append('from', '');
    params.append('channel', 'web');
    params.append('campaign', 'calculator-lead');
    // Add all saved UTM params
    const utms = getSavedUtmParams();
    Object.entries(utms).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    params.append('source', window.location.href);

    console.log("[DEBUG] Sendy request payload:", params.toString());

    // Step 3: Submit data
    const response = await fetch(SENDY_SUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cookie': cookies,
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: params
    });

    console.log("[DEBUG] Sendy response status:", response.status);
    const responseData = await response.json();
    console.log("[DEBUG] Sendy API response:", responseData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Send to newsendy API (non-blocking, same as ContactUs.js which has no try-catch)
    sendToSendy().catch(err => console.error('Sendy API error (non-blocking):', err));

    // Also send to Django growth API (non-blocking, best-effort)
    const pageUrl = window.location.pathname;
    const pageTitle = document.title;
    fetch('https://regular.maxiom.in/app/api/growth/calculator-leads/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
        calculator_type: calculatorType,
        calculator_results: {
          ...calculatorResults,
          page_url: pageUrl,
          page_title: pageTitle,
          device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
        },
        source: `calculator_${calculatorType}:${pageUrl}`,
        timestamp: new Date().toISOString(),
        otp_verified_mobile: otpVerified,
      })
    }).catch(err => console.error('Growth API error (non-blocking):', err));

    // Show success immediately (same as ContactUs.js behavior)
    setIsSuccess(true);
    setIsSubmitting(false);

    // Track event in GA4
    if (window.gtag) {
      window.gtag('event', 'lead_captured', {
        event_category: 'engagement',
        event_label: calculatorType,
        value: 1
      });
    }

    // Callback to parent component
    if (onLeadCaptured) {
      onLeadCaptured(formData);
    }

    // Show success for 2 seconds then close
    setTimeout(() => {
      onClose();
      setIsSuccess(false);
      setFormData({ name: '', email: '', phone: '' });
      setOtp('');
      setOtpSent(false);
      setOtpVerified(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, phone: newPhone }));

    // Clear error
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }

    // Auto-send OTP when 10 digits entered
    if (newPhone.length === 10 && !otpSent) {
      sendOTP(newPhone);
    }
  };

  // Auto-save form data to localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('leadFormData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    if (formData.email || formData.name) {
      localStorage.setItem('leadFormData', JSON.stringify(formData));
    }
  }, [formData]);

  if (!isOpen) return null;

  return (
    <div className="lead-modal-overlay" onClick={onClose}>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 10001 }}
      />
      <div className="lead-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="lead-modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        {isSuccess ? (
          <div className="lead-success-message">
            <div className="success-icon">
              <Check size={48} color="#4CAF50" />
            </div>
            <h2>Success!</h2>
            <p>Your personalized report has been sent to your email.</p>
            <p>Our advisor will contact you within 24 hours.</p>
          </div>
        ) : (
          <>
            <div className="lead-modal-header">
              <h2>{getHeadline()}</h2>
              <p>Free PDF report based on your calculations</p>
            </div>

            <div className="lead-benefits">
              <h3>You'll receive:</h3>
              <ul>
                {getBenefits().map((benefit, index) => (
                  <li key={index}>
                    <Check size={16} color="#4CAF50" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={handleSubmit} className="lead-form">
              <div className="form-group">
                <div className="input-wrapper">
                  <User size={20} />
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={errors.name ? 'error' : ''}
                  />
                </div>
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <Mail size={20} />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                  />
                </div>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <Phone size={20} />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Mobile Number *"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    onKeyDown={(e) => {
                      // Prevent Enter from submitting form during phone entry
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    className={errors.phone ? 'error' : ''}
                    maxLength={10}
                    disabled={otpSent}
                  />
                </div>
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              {/* OTP Input Section */}
              {otpSent && !otpVerified && (
                <div className="form-group">
                  <div className="otp-input-section">
                    <label className="otp-label">Enter 4-Digit OTP *</label>
                    <div className="otp-input-row">
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => {
                          const newOtp = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setOtp(newOtp);
                          if (newOtp.length === 4) {
                            verifyOTP(newOtp);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Prevent Enter key from submitting the form while in OTP field
                          if (e.key === 'Enter') {
                            e.preventDefault();
                          }
                        }}
                        className="otp-input"
                        placeholder="Enter OTP"
                        maxLength={4}
                        disabled={otpLoading}
                      />
                      <button
                        type="button"
                        className="otp-action-btn"
                        onClick={() => {
                          setOtp('');
                          sendOTP(formData.phone);
                        }}
                        disabled={otpLoading}
                      >
                        Resend OTP
                      </button>
                      <button
                        type="button"
                        className="otp-action-btn"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                          setFormData(prev => ({ ...prev, phone: '' }));
                        }}
                      >
                        Change Number
                      </button>
                    </div>
                    {otpLoading && (
                      <p className="otp-loading-text">Verifying...</p>
                    )}
                  </div>
                </div>
              )}

              {/* OTP Verified Success */}
              {otpVerified && (
                <div className="otp-verified-badge">
                  <Check size={16} color="#16a34a" />
                  <span>Mobile number verified successfully!</span>
                </div>
              )}

              {errors.submit && (
                <div className="error-message submit-error">{errors.submit}</div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || !otpVerified}
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : !otpVerified ? (
                  <span>Verify Mobile to Send Report</span>
                ) : (
                  <>
                    <FileText size={20} />
                    <span>Send Report</span>
                  </>
                )}
              </button>
            </form>

            <div className="lead-modal-footer">
              <Shield size={16} />
              <span>Your information is secure. No spam, unsubscribe anytime.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
